import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Divider, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import FixStickyTable from '@/components/FixStickyTable';
import CustomForm, { IFieldItem, IMyFormRef } from '@/components/CustomForm';
import RangeDateDynamicLimit from '@/components/RangeDateDynamicLimit';
import { EmptyHint } from '@/components/EmptyHint';
import useChannelOrderDetail from '@/pages/channel/channelOrder/centralReservationOrder/channelOrderDetail';
import { fetchPointsFlowPage } from '@/api/guest';
import useOrderLink from '@/hooks/useOrderLink';
import { filterFormParams, renderIntlLabel, storage } from '@/utils';
import { format2Integer } from '@/utils/number';
import {
  DATE_TIME_FORMAT,
  HotelPointsOperate,
  HotelPointsOperateList,
  HotelPointsOperateMap,
  OrderType,
  SysSource,
} from '@/config';
import styles from './index.module.scss';

const getDefaultFormValues = () => {
  return {
    businessDate: [moment().subtract(3, 'month'), moment()],
  };
};

const HotelPoints = ({ guestInfo, onHotelPointsClick }) => {
  const [result, setResult] = useState<{
    list: any[];
    pageNum: number;
    pageSize: number;
    total: number;
  }>({
    list: [],
    pageNum: 1,
    pageSize: 50,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const searchRef = useRef<IMyFormRef>();

  const ChannelOrderDetailModal = useChannelOrderDetail();

  const initHotelPointsPage = async (data) => {
    const params = {
      wyuid: guestInfo?.wyuid,
      pageNum: 1,
      pageSize: 50,
      ...data,
    };
    if (params.businessDate) {
      const start = moment(params.businessDate?.[0]).millisecond(0).valueOf();
      const end = moment(params.businessDate?.[1]).millisecond(0).valueOf();
      params.startTime = start;
      params.endTime = end;
      delete params.businessDate;
    }
    try {
      setLoading(true);
      const res = await fetchPointsFlowPage(filterFormParams(params));
      if (res.code === 0) {
        const { list, total } = res.data || {};
        setResult({ list, pageNum: params.pageNum, pageSize: params.pageSize, total });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const onReset = () => {
    const defaultFormValues = getDefaultFormValues();
    searchRef.current.setFieldsValue(defaultFormValues);
    initHotelPointsPage(defaultFormValues);
  };

  const handleBlur = () => {
    const values = searchRef.current.getFieldsValue();
    initHotelPointsPage(values);
  };

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    const values = searchRef.current.getFieldsValue();
    initHotelPointsPage({ pageNum: current, pageSize, ...values });
  };

  useEffect(() => {
    const defaultFormValues = getDefaultFormValues();
    searchRef.current.setFieldsValue(defaultFormValues);
    initHotelPointsPage(defaultFormValues);
  }, [guestInfo.wyuid]);

  const formAttr: Array<IFieldItem> = useMemo(() => {
    return [
      {
        label: renderIntlLabel('order_selfService_detail_billType'),
        key: 'type',
        type: 'select',
        dataSource: HotelPointsOperateList,
        componentProps: {
          style: {
            width: 240,
          },
          onBlur: handleBlur,
        },
      },
      {
        label: renderIntlLabel('order_selfService_detail_serviceTime'),
        key: 'businessDate',
        type: 'group',
        component: (
          <RangeDateDynamicLimit
            defaultRangeDate={[
              moment().subtract(3, 'year'),
              moment().add(1000, 'year').hour(23).minute(59).second(59),
            ]}
            rangeCount={180}
            isShowTime
            onBlur={handleBlur}
          />
        ),
      },
      {
        label: renderIntlLabel('guest_hotel_points_transaction_no'),
        key: 'no',
        componentProps: {
          maxLength: 100,
          onBlur: handleBlur,
        },
      },
    ];
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: renderIntlLabel('order_selfService_detail_billType'),
      dataIndex: 'type',
      render: (val) => HotelPointsOperateMap[val],
    },
    {
      title: renderIntlLabel('order_selfService_detail_serviceTime'),
      dataIndex: 'updateTime',
      render: (val) => (val ? moment(val).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      title: renderIntlLabel('order_selfService_detail_priorPeriodCash'),
      dataIndex: 'preBalancePoints',
    },
    {
      title: renderIntlLabel('guest_hotel_points_amount_of_variation'),
      dataIndex: 'currentBalancePoints',
      render: (val, record) =>
        val - record.preBalancePoints > 0
          ? `+${format2Integer(val - record.preBalancePoints)}`
          : format2Integer(val - record.preBalancePoints),
    },
    {
      title: renderIntlLabel('order_selfService_detail_currentPeriodCash'),
      dataIndex: 'currentBalancePoints',
    },
    { title: renderIntlLabel('order_commonCode'), dataIndex: 'commonCode' },
    { title: renderIntlLabel('order_selfService_detail_source'), dataIndex: 'appSource' },
    { title: renderIntlLabel('guest_hotel_points_operate_detail'), dataIndex: 'operateDetail' },
    { title: renderIntlLabel('guest_hotel_points_transaction_no'), dataIndex: 'no' },
    {
      title: renderIntlLabel('order_action'),
      render: (_, record) => (
        <>
          <div className={styles.actionBtns}>
            {record?.commonCode === storage.get('commonCode') &&
              record?.appSource === SysSource.CRS &&
              [HotelPointsOperate.Get, HotelPointsOperate.Use].includes(record?.type) && (
                <>
                  <Button
                    type="link"
                    onClick={() => ChannelOrderDetailModal.showDrawer({ orderNo: record.eventNo })}
                  >
                    {renderIntlLabel('order_action_channel_order_detail')}
                  </Button>
                </>
              )}

            {record?.commonCode === storage.get('commonCode') &&
              record?.appSource === SysSource.CRS &&
              [
                HotelPointsOperate.Get,
                HotelPointsOperate.Deduct,
                HotelPointsOperate.Return,
              ].includes(record?.type) && (
                <>
                  <Button type="link" onClick={() => onHotelPointsClick(record)}>
                    {renderIntlLabel('order_action_roomorder_detail')}
                  </Button>
                </>
              )}
          </div>
        </>
      ),
      align: 'right',
      fixed: 'right',
    },
  ];

  const { list, pageNum, pageSize, total } = result;

  return (
    <>
      <div className={styles.hotekContainer}>
        <CustomForm
          ref={searchRef}
          layout="inline"
          submitType="filter"
          formAttr={formAttr}
          onReset={onReset}
          showBtn
          noSearch
        />
        {Array.isArray(list) && !!list.length ? (
          <FixStickyTable>
            <Table
              rowKey="id"
              columns={columns}
              loading={loading}
              dataSource={list}
              pagination={{ current: pageNum, pageSize, total }}
              scroll={{ x: 'max-content', y: 800 }}
              style={{ marginTop: '16px' }}
              onChange={handleTableChange}
            />
          </FixStickyTable>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 200 }}>
            <EmptyHint hintText={renderIntlLabel('app_no_data')} />
          </div>
        )}
      </div>
    </>
  );
};

export default HotelPoints;
